import { FARO_CARD_IMAGE_OVERLAY_CLASS } from "@components/common/faro-card/faro-card";
import { useCompanyId } from "@context-providers/company-id-provider";
import { neutral, NO_TRANSLATE_CLASS } from "@flat-ui";
import { useLazyImage } from "@hooks/use-lazy-image";
import { Box, Skeleton } from "@mui/material";
import { ImageContextType } from "@services/image-manager";
import { EDecimalToHex } from "@styles/common-colors";
import { addTransparency } from "@utils/ui-utils";
import { useEffect, useState } from "react";

/** Height for the card thumbnail image */
export const IMAGE_HEIGHT = "168px";

interface Props {
  /** The src for the image to be displayed */
  src: string;

  /** The context type of the image */
  contextType: ImageContextType;

  /** Unique identifier for the context */
  contextId: string;

  /** Alternative text for accessibility (screen readers) */
  alt?: string;

  /** If this property is present the alternative text should not be translated with localize */
  shouldNotTranslate?: boolean;
}

/**
 * Component that shows the image of a card.
 * It has an overlay that is shown on hover, and makes sure to fit the image to the size of the card.
 */
export function FaroCardImage({
  src,
  contextType,
  contextId,
  alt = "",
  shouldNotTranslate = false,
}: Props): JSX.Element {
  const [isLoaded, setIsLoaded] = useState(false);
  const companyId = useCompanyId();
  const companyIdCacheKey = `${companyId}`;

  const { imageUrl, hasError } = useLazyImage(
    src,
    companyIdCacheKey,
    contextType,
    contextId
  );

  useEffect(() => {
    if (imageUrl !== null || hasError) {
      setIsLoaded(true);
    }
  }, [imageUrl, hasError]);

  return (
    <Box component="div" position="relative">
      <Skeleton
        variant="rectangular"
        height={IMAGE_HEIGHT}
        width="100%"
        sx={{
          position: "absolute",
          display: isLoaded ? "none" : "block",
        }}
      />
      {/* Overlay on top of the image that is shown on hover
          This makes that images with white background have the exact same gray background color
          as the middle and bottom part of the card */}
      <Box
        component="div"
        className={FARO_CARD_IMAGE_OVERLAY_CLASS}
        sx={{
          // Hidden by default and it is overridden to visible by the parent component on hover.
          visibility: "hidden",
          height: IMAGE_HEIGHT,
          width: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          backgroundColor: addTransparency({
            color: neutral[100],
            alpha: EDecimalToHex.twentySix,
          }),
        }}
      />

      <Box
        className={shouldNotTranslate ? NO_TRANSLATE_CLASS : undefined}
        height={IMAGE_HEIGHT}
        width="100%"
        sx={{
          alignContent: "center",
        }}
      >
        <Box
          component="img"
          src={hasError ? undefined : imageUrl ?? undefined}
          alt={alt}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          sx={{
            maxHeight: "100%",
            maxWidth: "100%",
            display: isLoaded ? "block" : "none",
            justifySelf: "center",
          }}
        />
      </Box>
    </Box>
  );
}
